import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MailX, Pencil, Plus, UserCheck, UserX } from "lucide-react";
import { ROLES, type AdminUser, type ManagerColor } from "@salesboard/contracts";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { DataTable, type Column } from "@/components/ui/data-table/DataTable";
import { ListToolbar } from "@/components/ui/data-table/ListToolbar";
import { PaginationControl } from "@/components/ui/data-table/PaginationControl";
import { RowActions, type RowAction } from "@/components/ui/RowActions";
import { StatusChip, type ChipTone } from "@/components/ui/StatusChip";
import { EmptyState, ErrorState, LoadingState, NoResultState } from "@/components/ui/states";
import { useServerTable, type FilterDefs } from "@/hooks/useServerTable";
import { ApiClientError } from "@/lib/api";
import { formatDateTime, formatSignIn } from "@/lib/format";
import { useSession } from "@/features/auth/useSession";
import { Swatch } from "@/components/ui/ColourPicker";
import { InvitationLink, UserEditDialog, UserFormDialog } from "./UserFormDialog";
import { useAdminUsers, useInviteUser, useRevokeInvitation, useSetUserActive } from "./hooks";

export function UsersPage(): React.JSX.Element {
  const t = useT();
  const session = useSession();

  useDocumentTitle(t("users.title"));

  // Inside the component now that the labels are translated, memoised on the translator
  // because the filter seam and the table key their memos on this object.
  const FILTERS: FilterDefs = useMemo(
    () => ({
      q: { type: "search" },
      role: {
        type: "select",
        label: t("filter.role"),
        options: [
          { value: ROLES.headOfSales, label: t("role.head_of_sales") },
          { value: ROLES.salesManager, label: t("role.sales_manager") },
        ],
      },
      state: {
        type: "select",
        label: t("filter.state"),
        options: [
          { value: "invited", label: t("userState.invited") },
          { value: "active", label: t("userState.active") },
          { value: "deactivated", label: t("userState.deactivated") },
        ],
      },
    }),
    [t],
  );

  const table = useServerTable("users", FILTERS, { field: "name", direction: "asc" });
  const query = useAdminUsers(table.queryParams);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser>();
  const [deactivating, setDeactivating] = useState<AdminUser>();
  const [issuedLink, setIssuedLink] = useState<{ user: AdminUser; link: string; expiresAt: string }>();

  const invite = useInviteUser();
  const revoke = useRevokeInvitation();
  const setActive = useSetUserActive();

  // The duplicate-colour warning is answered over the whole active team, not the rows on
  // screen: one that falls silent because the other holder of the colour is on page two —
  // or excluded by a filter — is worse than none, because silence reads as "unique".
  const team = useAdminUsers({ perPage: 100, state: "active", sort: "name:asc" });

  // Counted per user id across both sources, so a user present in both is one holder and
  // not two, and so a failed team query degrades to the visible rows rather than to nothing.
  // Deactivated users are excluded, as the removed Settings section excluded them.
  const colourByUser = new Map<string, ManagerColor>();
  for (const member of [...(team.data?.items ?? []), ...(query.data?.items ?? [])]) {
    if (member.active && member.color !== null) colourByUser.set(member.id, member.color);
  }
  const holders = new Map<ManagerColor, number>();
  for (const color of colourByUser.values()) holders.set(color, (holders.get(color) ?? 0) + 1);
  const sharedColours = new Set([...holders].filter(([, count]) => count > 1).map(([color]) => color));

  const columns: Column<AdminUser>[] = [
    {
      key: "name",
      header: t("column.name"),
      sortable: true,
      render: (row) => (
        <span>
          <span className="font-medium">{row.name}</span>
          {row.id === session.data?.id && <span className="text-muted-foreground ml-1 text-xs">{t("common.you")}</span>}
        </span>
      ),
    },
    { key: "email", header: t("column.email"), sortable: true, render: (row) => row.email },
    {
      key: "role",
      header: t("column.role"),
      sortable: true,
      // Read, not edited (2026-08-26): a select in every row made the table a form, and the
      // columns carrying facts were pushed off the side of it. Edit opens a dialog.
      render: (row) => t(`role.${row.role}`),
    },
    {
      key: "color",
      header: t("column.colour"),
      /*
       * The colour as a fact, not a control. It was a swatch popover per row; picking now
       * happens in the edit dialog, and what stays here are the two things a reader needs to
       * *see* — which colour, and the two cues that something wants attention: nobody has one,
       * or two managers share one and the board cannot tell them apart.
       */
      render: (row) => (
        <div className="flex flex-col items-start gap-0.5">
          <span className="flex items-center gap-2 text-sm">
            <Swatch color={row.color} />
            {t(row.color ? `colour.${row.color}` : "colour.none")}
          </span>
          {/* The *needs colour* cue is gone (2026-08-26, asked for): an unassigned colour is
              already legible as "None" beside a hollow swatch, and a standing warning about
              something nobody has to do yet read as an error. The shared-colour cue stays —
              two managers on one hue is a board that cannot tell them apart, which is a
              defect in the data rather than an invitation to tidy up. */}
          {row.color !== null && sharedColours.has(row.color) && (
            <span className="text-warning text-xs">{t("colour.sharedWarning")}</span>
          )}
        </div>
      ),
    },
    {
      key: "state",
      header: t("column.state"),
      // Derived, never stored: an invited user who has never signed in is neither active
      // nor deactivated, and an administrator needs to see the difference — which is why
      // this column carries three tones where clients and stages carry two.
      render: (row) => <StateCell user={row} />,
    },
    {
      key: "openOpportunities",
      header: t("column.openOpportunities"),
      align: "right",
      /*
       * The count leads to the cards it counts: the board, filtered to this manager.
       *
       * A real `<Link>`, not a click handler — the destination is a *filtered board*, which
       * is a URL anyone can share and a middle click can open in a tab. It deliberately does
       * not go through `openDetail`, which is for the modals that layer over a surface; this
       * replaces the surface.
       *
       * A zero stays plain text. There is nothing behind it, and a pressable nothing is the
       * dead end the strip's tiles were fixed for.
       */
      render: (row) =>
        row.openOpportunities > 0 ? (
          <Link
            to={`/?opportunities:owner=${row.id}`}
            className="hover:text-foreground focus-visible:ring-ring rounded underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:outline-none"
            aria-label={t("users.openOpportunitiesFor", row.name, row.openOpportunities)}
          >
            {row.openOpportunities}
          </Link>
        ) : (
          row.openOpportunities
        ),
    },
    {
      key: "lastSignInAt",
      header: t("column.lastSignIn"),
      sortable: true,
      render: (row) => formatSignIn(row.lastSignInAt),
    },
    {
      key: "actions",
      header: "",
      /*
       * One trigger per row (2026-08-26). Three icon buttons per line made every row a small
       * toolbar; a menu names its actions in words instead, and Edit — the action that replaced
       * the inline select and swatch — has somewhere to live.
       */
      render: (row) => {
        const actions: RowAction[] = [
          { label: t("common.edit"), icon: Pencil, onSelect: () => setEditing(row) },
        ];

        if (row.state !== "deactivated") {
          actions.push({
            label: row.invitedAt ? t("users.reinvite") : t("users.invite"),
            icon: Mail,
            onSelect: () => {
              void invite
                .mutateAsync({ id: row.id, send: true })
                .then((result) => setIssuedLink({ user: row, link: result.link, expiresAt: result.expiresAt }));
            },
          });
        }

        if (row.invitedAt && row.state === "invited") {
          actions.push({
            label: t("users.revokeInvitation", row.name),
            icon: MailX,
            onSelect: () => revoke.mutate(row.id),
          });
        }

        actions.push({
          label: row.active ? t("common.deactivate") : t("common.reactivate"),
          icon: row.active ? UserX : UserCheck,
          destructive: row.active,
          // The reader's own row: deactivating yourself locks you out of the screen that
          // would let you back in, so the item says why rather than disappearing.
          disabledReason: row.id === session.data?.id ? t("users.ownStateLocked") : undefined,
          onSelect: () => {
            if (row.active) setDeactivating(row);
            else setActive.mutate({ id: row.id, active: true, version: row.version });
          },
        });

        return (
          <div className="flex justify-end">
            <RowActions label={t("users.rowActions", row.name)} actions={actions} />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <h1 className="sr-only">{t("users.title")}</h1>

      <ListToolbar
        table={table}
        defs={FILTERS}
        searchPlaceholder={t("users.searchPlaceholder")}
        actions={
          // Settings is reached from the profile menu now (2026-08-26), not from a button on
          // this screen: it configures the application, not the people on this list.
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t("users.new")}
          </Button>
        }
      />

      {query.isPending && <LoadingState rows={5} label={t("users.loading")} />}

      {query.error && (
        <ErrorState
          message={query.error instanceof ApiClientError ? query.error.message : t("users.loadFailed")}
          requestId={query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
          onRetry={() => void query.refetch()}
        />
      )}

      {query.data && query.data.items.length === 0 && table.activeFilterCount > 0 && (
        <NoResultState activeFilters={table.activeFilterLabels} onClear={table.clearFilters} />
      )}

      {query.data && query.data.items.length === 0 && table.activeFilterCount === 0 && (
        <EmptyState
          title={t("users.emptyTitle")}
          description={t("users.emptyDescription")}
          action={{ label: t("users.new"), onClick: () => setCreateOpen(true) }}
        />
      )}

      {query.data && query.data.items.length > 0 && (
        <>
          <DataTable
            columns={columns}
            rows={query.data.items}
            rowKey={(row) => row.id}
            sort={table.sort}
            onToggleSort={table.toggleSort}
            caption={t("users.caption")}
          />
          <PaginationControl
            page={query.data.page}
            perPage={query.data.perPage}
            total={query.data.total}
            onPageChange={table.setPage}
            onPerPageChange={table.setPerPage}
          />
        </>
      )}

      {createOpen && <UserFormDialog open={createOpen} onOpenChange={setCreateOpen} />}

      {editing && (
        <UserEditDialog
          user={editing}
          isSelf={editing.id === session.data?.id}
          onOpenChange={(next) => !next && setEditing(undefined)}
        />
      )}

      {/* The re-issued link is shown once; there is no way to recover it afterwards. */}
      <ConfirmDialog
        open={Boolean(issuedLink)}
        onOpenChange={() => setIssuedLink(undefined)}
        title={issuedLink ? t("users.invitationTitle", issuedLink.user.name) : ""}
        consequence={t("users.invitationConsequence")}
        confirmLabel={t("common.done")}
        onConfirm={() => setIssuedLink(undefined)}
        cancelLabel={t("common.close")}
      />

      {issuedLink && (
        <div className="fixed bottom-24 left-1/2 z-20 w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2">
          <div className="bg-card space-y-2 rounded-lg border p-4 shadow-lg">
            <p className="text-sm font-medium">{t("users.invitationLinkFor", issuedLink.user.name)}</p>
            <InvitationLink link={issuedLink.link} expiresAt={issuedLink.expiresAt} />
            <Button variant="ghost" size="sm" onClick={() => setIssuedLink(undefined)}>
              {t("common.dismiss")}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deactivating)}
        onOpenChange={(next) => !next && setDeactivating(undefined)}
        title={deactivating ? t("users.deactivateTitle", deactivating.name) : ""}
        consequence={
          deactivating
            ? t("users.deactivateConsequence", t("count.opportunities", deactivating.openOpportunities))
            : ""
        }
        confirmLabel={t("users.deactivateConfirm")}
        destructive
        pending={setActive.isPending}
        onConfirm={async () => {
          if (!deactivating) return;
          await setActive.mutateAsync({ id: deactivating.id, active: false, version: deactivating.version });
          setDeactivating(undefined);
        }}
      />
    </>
  );
}

/** State carries a word, never colour alone. */
/** Which of the chip's tones each state wears. Invited is neither of the other two. */
const STATE_TONE: Record<AdminUser["state"], ChipTone> = {
  active: "positive",
  invited: "warning",
  deactivated: "neutral",
};

function StateCell({ user }: { user: AdminUser }) {
  const t = useT();

  return (
    <span>
      {/* The shared chip, as on clients and stages (2026-08-26). Three tones rather than the
          chip's two, because an invitation nobody has accepted wants attention rather than
          approval or silence — and the word still carries the state, so the hue is never
          doing the work on its own. */}
      <StatusChip
        label={t(`userState.${user.state}`)}
        on={user.state === "active"}
        tone={STATE_TONE[user.state]}
      />
      {user.state === "invited" && user.invitedAt && (
        <span className="text-muted-foreground block text-xs">
          {t("users.invitedAt", formatDateTime(user.invitedAt))}
        </span>
      )}
      {user.state === "active" && !user.credentialSet && (
        <span className="text-muted-foreground block text-xs">{t("users.entraOnly")}</span>
      )}
    </span>
  );
}
